import { Box } from '@dtx-company/shared-components/src/components/atoms/Box/index'
import { ContextMenu } from '@dtx-company/shared-components/src'
import { FC } from 'react'
import { GenericButton } from '@dtx-company/shared-components/src/components/atoms/Button/index'
import { GetAncestors } from '../myflowcodes/CreateFolderV2/CreateFolderV2.hooks'
import { Icon } from '@dtx-company/shared-components/src/components/atoms/Icon/index'
import { Menu } from '@dtx-company/shared-components/src/components/atoms/Menu/index'
import { Text } from '@dtx-company/shared-components/src/components/atoms/Text/index'
import { getIdFromEncodedString } from '@dtx-company/true-common/src/utils/strings'
import { useFolderBreadcrumbsState } from './FolderBreadcrumbs.hooks'
import Skeleton from 'react-loading-skeleton'

const MAX_FOLDER_BREADCRUMBS_LENGTH = 3

export interface FolderBreadCrumbsProps {
  ancestors: GetAncestors['ancestors']
  activeFolderId: string
  hideBatchCount?: boolean
  maxLength?: number
  enableBreadcrumbNavigation?: boolean
  rootFolderLabel?: string
  handleBreadcrumbClick?: (id: string, isRootFolder?: boolean) => void
  hideFolderIcon?: boolean
  loading?: boolean
}

export const FolderBreadCrumbs: FC<FolderBreadCrumbsProps> = ({
  ancestors,
  activeFolderId,
  hideBatchCount,
  maxLength = MAX_FOLDER_BREADCRUMBS_LENGTH,
  enableBreadcrumbNavigation = false,
  rootFolderLabel,
  handleBreadcrumbClick,
  hideFolderIcon = false,
  loading
}) => {
  const {
    displayHiddenFolders,
    hiddenFolderActions,
    hiddenFolderMenuRef,
    hiddenFolders,
    visibleFolders,
    toggleHiddenFolders
  } = useFolderBreadcrumbsState({
    ancestors,
    maxLength,
    enableBreadcrumbNavigation,
    handleBreadcrumbClick
  })

  if (loading) {
    return <Skeleton height="20px" width="100%" />
  }

  return (
    <Box alignItems="center">
      <GenericButton
        disabled={!enableBreadcrumbNavigation}
        onClick={() => handleBreadcrumbClick?.('', true)}
      >
        {!hideFolderIcon && (
          <Icon height="16px" src="/icons/folder-outlined-grey.svg" pr="4px" alt="" />
        )}
        {rootFolderLabel && (
          <Text fontSize="12px" color="primary.grey" pr="4px">
            {rootFolderLabel}
          </Text>
        )}
      </GenericButton>
      {Boolean(hiddenFolders?.length) && (
        <>
          <GenericButton onClick={toggleHiddenFolders} title="Display truncated folders">
            <Text as="span" color="primary.grey" mx="5px">
              ...
            </Text>
          </GenericButton>
          {displayHiddenFolders && (
            <Box position="relative" ref={hiddenFolderMenuRef}>
              <ContextMenu
                position="absolute"
                zIndex={1}
                left="0"
                top="12px"
                px={0}
                actions={hiddenFolderActions}
                maxWidth="250px"
                enableMiddleBorders
              />
            </Box>
          )}
        </>
      )}

      <Menu display="flex">
        {visibleFolders?.map(folder => {
          const id = getIdFromEncodedString(folder?.id || '')
          return (
            <li key={folder?.id}>
              <Text
                color={activeFolderId === id ? 'primary.black' : 'primary.grey'}
                fontSize="12px"
                display="flex"
              >
                <Text as="span" color="primary.grey" fontSize="inherit">
                  /
                </Text>
                <GenericButton
                  disabled={!enableBreadcrumbNavigation}
                  onClick={() => handleBreadcrumbClick?.(folder?.id ?? '')}
                >
                  <Text as="span" px="4px" fontSize="inherit" color="inherit">
                    {folder?.name}
                    &nbsp;
                    {!hideBatchCount && <>({folder?.batchCount})</>}
                  </Text>
                </GenericButton>
              </Text>
            </li>
          )
        })}
      </Menu>
    </Box>
  )
}
